import { joinURL, withQuery } from 'ufo'

// Same origin mapping as `parseImgURL` (composables/image/useImage.ts) + the `imagekit` provider of
// NuxtImg (`image.imagekit.baseURL` in nuxt.config): ImageKit fetches the original from Firebase
// Storage, resizes it and serves WebP/AVIF to browsers that accept it.
const FIREBASE_STORAGE_ORIGIN = 'https://firebasestorage.googleapis.com'
const IMAGEKIT_BASE_URL = 'https://ik.imagekit.io/growni'

/**
 * Build a CDN-resized URL for an image of a known rendered size.
 *
 * Sources we know about:
 * - Unsplash (`images.unsplash.com`): supports `?w=&h=&q=&auto=format&fit=crop` natively (Imgix).
 * - Firebase Storage (`firebasestorage.googleapis.com`): resized through ImageKit. With both width and
 *   height the image is center-cropped to that box (like `object-cover`); with width only it keeps its ratio.
 * - Anything else (other hosts, `data:` URLs, local assets): returned as-is.
 */
export function resizedImageSrc(url: string | undefined, width: number, height?: number): string | undefined {
    if (!url) return undefined
    return resizeImage(url, width, height) ?? url
}

/**
 * Returns a `srcset` pair (1x, 2x) so retina screens get a sharper image without
 * shipping the 2x to everyone. Undefined for sources that can't be resized.
 */
export function resizedImageSrcset(url: string | undefined, width: number, height?: number): string | undefined {
    if (!url) return undefined
    const oneX = resizeImage(url, width, height)
    const twoX = resizeImage(url, width * 2, height ? height * 2 : undefined)
    if (!oneX || !twoX) return undefined
    return `${oneX} 1x, ${twoX} 2x`
}

function resizeImage(url: string, width: number, height?: number): string | undefined {
    return resizeUnsplash(url, width, height) ?? resizeFirebaseStorage(url, width, height)
}

function resizeUnsplash(url: string, width: number, height?: number): string | undefined {
    if (!url.includes('images.unsplash.com')) return undefined
    const base = url.split('?')[0]
    const params = new URLSearchParams()
    params.set('w', String(width))
    if (height) params.set('h', String(height))
    params.set('q', '75')
    params.set('auto', 'format')
    params.set('fit', 'crop')
    return `${base}?${params.toString()}`
}

function resizeFirebaseStorage(url: string, width: number, height?: number): string | undefined {
    if (!url.startsWith(FIREBASE_STORAGE_ORIGIN)) return undefined
    const path = url.slice(FIREBASE_STORAGE_ORIGIN.length)
    const transformation = height ? `w-${width},h-${height}` : `w-${width}`
    return joinURL(IMAGEKIT_BASE_URL, withQuery(path, { tr: transformation }))
}
